import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Music, MapPin, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import AppHeader from "@/components/AppHeader";

interface Venue {
  id: string;
  name: string;
  slug: string;
  welcome_message: string;
  primary_color: string;
  secondary_color: string;
  logo_url: string | null;
  created_at: string;
}

const VenueSelection = () => {
  const navigate = useNavigate();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadVenues();
  }, []);

  const loadVenues = async () => {
    try {
      const { data, error } = await supabase
        .from('venues')
        .select('*')
        .order('name');

      if (error) throw error;
      setVenues(data || []);
    } catch (error) {
      console.error('Error loading venues:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVenues = venues.filter(venue =>
    venue.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleVenueSelect = (venue: Venue) => {
    navigate(`/singer/${venue.slug}`);
  };

  return (
    <div className="min-h-screen bg-console-bg text-console-text font-geist p-6 relative">
      <div className="container mx-auto max-w-4xl space-y-8">
        <AppHeader
          title="🎤 Select Your Venue"
          subtitle="Choose a karaoke venue to start singing"
          showHomeButton={false}
        />

        {/* Search */}
        <Card className="bg-console-surface border border-console-line rounded-[10px]">
          <CardContent className="p-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search venues..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim focus:border-console-primary focus-visible:ring-0 focus-visible:ring-offset-0 font-geist rounded-[6px]"
              />
            </div>
          </CardContent>
        </Card>

        {/* Venues Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading venues...</p>
          </div>
        ) : filteredVenues.length === 0 ? (
          <Card className="bg-console-surface border border-console-line rounded-[10px]">
            <CardContent className="text-center py-12">
              <Music className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-semibold mb-2">No Venues Found</h3>
              <p className="text-muted-foreground mb-6">
                {searchTerm ? 'No venues match your search.' : 'No karaoke venues are currently available.'}
              </p>
              {searchTerm && (
                <Button 
                  variant="outline" 
                  onClick={() => setSearchTerm('')}
                >
                  Clear Search
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVenues.map((venue) => (
              <Card 
                key={venue.id} 
                className="bg-console-surface border border-console-line rounded-[10px] hover:scale-105 transition-transform cursor-pointer group"
                onClick={() => handleVenueSelect(venue)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: venue.primary_color || '#10b981' }}
                    >
                      <Music className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg group-hover:text-primary transition-colors">
                        {venue.name}
                      </CardTitle>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        <span>/{venue.slug}</span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="mb-4 line-clamp-2">
                    {venue.welcome_message || "Welcome to our karaoke venue!"}
                  </CardDescription>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Users className="h-4 w-4" />
                      <span>Live Queue</span>
                    </div>
                    <Button 
                      size="sm" 
                      className="h-11 sm:h-9 px-4 font-geist font-semibold text-[13px] bg-console-primary text-console-bg hover:bg-console-primary/90 rounded-[6px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVenueSelect(venue);
                      }}
                    >
                      Enter Venue
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info Card */}
        <Card className="bg-console-surface border border-console-line rounded-[10px] border-primary/20">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                <Music className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold mb-2">How it works</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Select a venue to access their song catalog</li>
                  <li>• Browse and search available songs</li>
                  <li>• Submit your karaoke requests</li>
                  <li>• Watch the live queue for your turn</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VenueSelection;
